import { FileDownload } from '@shared/http';

import { MarketBasket } from '@/features/market-baskets';
import { CreateMarketBasketFormInputs } from '@/features/market-baskets/types/market-basket-form-inputs';
import { useFileDownload } from '@/hooks/use-file-download';
import { artiaProtectedApi } from '@/lib/api-client';
import { deleteWithValidation, downloadFile, postWithValidation, putWithValidation, RequestOptions } from '@/lib/api-helpers';

type CreateMarketBasketRequest = {
  name: string;
  clientId: number;
  currentCpiu: number;
  currentCpiuDate: string;
  purpose: string;
  drugId?: number | null;
};

export type UpdateMarketBasketRequest = {
  name: string;
  currentCpiu: number;
  currentCpiuDate: string;
  purpose: string;
  drugId?: number | null;
};

export type UpdateOverridesRequest = {
  wacPrice?: string,
  wacUnitPrice?: string,
  amp?: string,
  totalUra?: string,
  uroa?: string
};

export type AddDrugPackagingsRequest = {
  drugId?: number;
  coverageTagIds?: number[];
  includeUntagged?: boolean;
  ndcs?: string[];
};

export type AddDrugPackagingsResult = {
  added: number;
  missingWac: number;
  noneFound: boolean;
};

export type MarketBasketBidAnalysis = {
  id: number;
  slug: string;
  name: string;
  productName: string;
  status: string;
  createdAt: string;
};

export const useMarketBasketService = () => {
  const associateUnknownPackagings = (drugId: number, ndcs: string[]): Promise<void> => {
    return postWithValidation('market-baskets/associate-packagings', {
      data: {
        drugId: drugId,
        ndcs: ndcs
      }
    });
  };

  const getMarketBasket = (id: number): Promise<MarketBasket> => {
    return artiaProtectedApi.get(`market-baskets/${id}`);
  };

  const getMarketBaskets = (): Promise<MarketBasket[]> => {
    return artiaProtectedApi.get('market-baskets');
  };

  const deleteMarketBasketRows = (id: number, ndcs: string[]): Promise<void> => {
    return deleteWithValidation(`market-baskets/${id}/market-details`, {
      data: ndcs
    });
  };

  const downloadAttachment = useFileDownload();

  const createMarketBasket = (form: CreateMarketBasketFormInputs): Promise<any> => {
    const data: CreateMarketBasketRequest = {
      // @ts-expect-error TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
      name: form.name?.length > 0 ? form.name : null,
      clientId: form.client?.id,
      currentCpiu: parseFloat(form.currentCpiu),
      currentCpiuDate: form.currentCpiuDate.slice(0, 10),
      purpose: form.purpose,
      drugId: form.drug?.id ?? null
    };

    const options: RequestOptions<any> = {
      data
    };

    return postWithValidation('market-baskets', options);
  };

  const updateMarketBasket = (marketBasketId: number | string, request: UpdateMarketBasketRequest): Promise<any> => {
    const options: RequestOptions<any> = {
      data: request
    };

    return putWithValidation(`market-baskets/${marketBasketId}`, options);
  };

  const archiveMarketBasket = (marketBasketId: number): Promise<any> => {
    return putWithValidation(`market-baskets/${marketBasketId}/archive`);
  };

  const deleteMarketBasket = (marketBasketId: number): Promise<any> => {
    return deleteWithValidation(`market-baskets/${marketBasketId}`);
  };

  const exportMarketBasket = (id: number): Promise<FileDownload> => {
    return downloadFile('POST', `market-baskets/${id}/export`);
  };

  const uploadMarketDetailsFile = (file: File, marketBasketId: number): Promise<any> => {
    const data = new FormData();

    data.append('Upload', file);

    const options: RequestOptions<any> = {
      data
    };

    return postWithValidation(`market-baskets/${marketBasketId}/import-market-details`, options);
  };

  const uploadAttachment = (file: File, marketBasketId: number): Promise<any> => {
    const data = new FormData();

    data.append('Upload', file);

    const options: RequestOptions<any> = {
      data
    };

    return postWithValidation(`market-baskets/${marketBasketId}/attachments`, options);
  };

  const addDrugPackagings = (marketBasketId: number, request: AddDrugPackagingsRequest): Promise<AddDrugPackagingsResult> => {
    return postWithValidation(`market-baskets/${marketBasketId}/add-drug-packagings`, { data: request });
  };

  const cloneMarketBasket = (marketBasketId: number, name: string): Promise<{ id: number }> => {
    return postWithValidation(`market-baskets/${marketBasketId}/clone`, { data: { name } });
  };

  const getBidAnalysesForMarketBasket = (marketBasketId: number): Promise<MarketBasketBidAnalysis[]> => {
    return artiaProtectedApi.get(`market-baskets/${marketBasketId}/bid-analyses`);
  };

  const recalculate = (marketBasketId: number): Promise<any> => {
    return postWithValidation(`market-baskets/${marketBasketId}/recalculate`);
  };

  const updateOverrides = (marketBasketId: number | string, ndc: string, request: UpdateOverridesRequest) => {
    const options: RequestOptions<any> = {
      data: request
    };

    return putWithValidation(`market-baskets/${marketBasketId}/packagings/${ndc}/overrides`, options);
  };

  return {
    addDrugPackagings,
    archiveMarketBasket,
    associateUnknownPackagings,
    cloneMarketBasket,
    getBidAnalysesForMarketBasket,
    getMarketBasket,
    getMarketBaskets,
    deleteMarketBasket,
    deleteMarketBasketRows,
    downloadAttachment,
    createMarketBasket,
    exportMarketBasket,
    updateMarketBasket,
    uploadAttachment,
    uploadMarketDetailsFile,
    updateOverrides,
    recalculate
  };
};
